import { useFormikContext } from 'formik';

import { legalPersonType, personType, translations, constValues } from './consts';
import {
    GenericObject,
    checkEmail,
    checkIfSerbianPhoneNumber,
    checkPhoneNumber,
    getBirthDateFromUCN,
    checkUCN,
    checkIfAdult,
    hasValueAndIsSalesForce,
} from '@fnol/library/src/lib';

export const useValidateIndividual = (
    individualType: personType,
): {
    errors: { [key: string]: string };
    touched: { [key: string]: boolean };
    isValid: boolean;
} => {
    const individualExistingErrors: { [key: string]: string } = {};
    const individualTouched: { [key: string]: boolean } = {};
    let individualIsValid = true;

    const { values, setFieldValue } = useFormikContext<GenericObject>();

    const individualPersonTypePropertyName = `${personType[individualType]}Type`;

    const role = `${personType[individualType]}IndividualPersonRole`;
    const name = `${personType[individualType]}IndividualName`;
    const lastname = `${personType[individualType]}IndividualLastName`;
    const dateOfBirth = `${personType[individualType]}IndividualDateOfBirth`;
    const ucn = `${personType[individualType]}IndividualUCN`;
    const country = `${personType[individualType]}IndividualCountry`;
    const city = `${personType[individualType]}IndividualCity`;
    const cityInSerbia = `${personType[individualType]}IndividualCityInSerbia`;
    const street = `${personType[individualType]}IndividualStreet`;
    const streetNumber = `${personType[individualType]}IndividualStreetNumber`;
    const email = `${personType[individualType]}IndividualEmail`;
    const phone = `${personType[individualType]}IndividualPhone`;

    if (values[individualPersonTypePropertyName] === legalPersonType.individual) {
        individualTouched[role] = true;
        individualTouched[name] = true;
        individualTouched[lastname] = true;
        individualTouched[dateOfBirth] = true;
        individualTouched[ucn] = true;
        individualTouched[country] = true;
        individualTouched[city] = true;
        individualTouched[cityInSerbia] = true;
        individualTouched[street] = true;
        individualTouched[streetNumber] = true;
        individualTouched[email] = true;
        individualTouched[phone] = true;

        if (!values[role]) {
            individualExistingErrors[role] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (
            values[role] &&
            !values.roles.some((item: GenericObject) => item.individual && item.id == values[role].id)
        ) {
            individualExistingErrors[role] = translations.validationMessages.INDIVIDUAL_ROLA_FIELD_INVALID;
            individualIsValid = false;
        }

        if (!values[name]) {
            individualExistingErrors[name] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[lastname]) {
            individualExistingErrors[lastname] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (
            !values[ucn] &&
            (values[country] === constValues.countrySerbiaId ||
                values[country] === constValues.countrySerbiaId.toString(10))
        ) {
            individualExistingErrors[ucn] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[dateOfBirth] && values[ucn]) {
            const birthDate = getBirthDateFromUCN(values[ucn]);
            if (!!birthDate) setFieldValue(dateOfBirth, birthDate);
        }

        if (values[dateOfBirth] && !checkIfAdult(values[dateOfBirth])) {
            individualExistingErrors[ucn] = translations.validationMessages.NOT_ADULT;
            individualIsValid = false;
        }

        if (
            values[country] === constValues.countrySerbiaId ||
            values[country] === constValues.countrySerbiaId.toString(10)
        ) {
            if (values[ucn] && !checkUCN(values[ucn])) {
                individualExistingErrors[ucn] = translations.validationMessages.UCN_INVALID_FORMAT;
                individualIsValid = false;
            }
        }

        if (values[country] === '') {
            individualExistingErrors[country] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (
            values[country] !== constValues.countrySerbiaId.toString(10) &&
            values[country] !== constValues.countrySerbiaId &&
            values[city] === ''
        ) {
            individualExistingErrors[city] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (
            (values[country] === constValues.countrySerbiaId.toString(10) ||
                values[country] === constValues.countrySerbiaId) &&
            (!values[cityInSerbia] || values[cityInSerbia].id === '')
        ) {
            individualExistingErrors[cityInSerbia] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (values[street] === '') {
            individualExistingErrors[street] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (values[streetNumber] === '') {
            individualExistingErrors[streetNumber] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (!values[email] && !hasValueAndIsSalesForce(values[phone])) {
            individualExistingErrors[email] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (values[email] && !checkEmail(values[email])) {
            // check only if there is a value
            individualExistingErrors[email] = translations.validationMessages.EMAIL_ADDRESS_FORMAT;
            individualIsValid = false;
        }

        if ((!values[phone] || values[phone].length < 5) && !hasValueAndIsSalesForce(values[email])) {
            individualExistingErrors[phone] = translations.validationMessages.REQUIRED_FIELD;
            individualIsValid = false;
        }

        if (
            (values[phone].length > 4 && !checkPhoneNumber(values[phone])) ||
            (values[phone].length > 4 &&
                values[country] === constValues.countrySerbiaId &&
                !checkIfSerbianPhoneNumber(values[phone]))
        ) {
            individualExistingErrors[phone] = translations.validationMessages.PHONE_NUMBER_FORMAT_SERBIAN;
            individualIsValid = false;
        }
    }
    return { errors: individualExistingErrors, touched: individualTouched, isValid: individualIsValid };
};

export const useValidateLegalEntity = (
    legalEntityType: personType,
): {
    errors: { [key: string]: string };
    touched: { [key: string]: boolean };
    isValid: boolean;
} => {
    const legalEntityExistingErrors: { [key: string]: string } = {};
    const legalEntityTouched: { [key: string]: boolean } = {};
    let legalEntityIsValid = true;

    const { values } = useFormikContext<any>();

    const legalPersonTypePropertyName = `${personType[legalEntityType]}Type`;

    const role = `${personType[legalEntityType]}LegalEntityPersonRole`;
    const name = `${personType[legalEntityType]}LegalEntityName`;
    const id = `${personType[legalEntityType]}LegalEntityID`;
    const vat = `${personType[legalEntityType]}LegalEntityVAT`;
    const country = `${personType[legalEntityType]}LegalEntityCountry`;
    const city = `${personType[legalEntityType]}LegalEntityCity`;
    const cityInSerbia = `${personType[legalEntityType]}LegalEntityCityInSerbia`;
    const street = `${personType[legalEntityType]}LegalEntityStreet`;
    const streetNumber = `${personType[legalEntityType]}LegalEntityStreetNumber`;
    const email = `${personType[legalEntityType]}LegalEntityEmail`;
    const contactName = `${personType[legalEntityType]}LegalEntityContactName`;
    const contactLastName = `${personType[legalEntityType]}LegalEntityContactLastName`;
    const contactEmail = `${personType[legalEntityType]}LegalEntityContactEmail`;
    const contactPhone = `${personType[legalEntityType]}LegalEntityContactPhone`;

    if (values[legalPersonTypePropertyName] === legalPersonType.legal) {
        legalEntityTouched[role] = true;
        legalEntityTouched[name] = true;
        legalEntityTouched[id] = true;
        legalEntityTouched[vat] = true;
        legalEntityTouched[country] = true;
        legalEntityTouched[city] = true;
        legalEntityTouched[cityInSerbia] = true;
        legalEntityTouched[street] = true;
        legalEntityTouched[streetNumber] = true;
        legalEntityTouched[email] = true;
        legalEntityTouched[contactName] = true;
        legalEntityTouched[contactLastName] = true;
        legalEntityTouched[contactEmail] = true;
        legalEntityTouched[contactPhone] = true;

        if (!values[role]) {
            legalEntityExistingErrors[role] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        }

        if (
            values[role] &&
            !values.roles.some((item: GenericObject) => item.legalEntity && item.id == values[role].id)
        ) {
            legalEntityExistingErrors[role] = translations.validationMessages.LEGAL_ENTITY_ROLA_FIELD_INVALID;
            legalEntityIsValid = false;
        }

        if (!values[name]) {
            legalEntityExistingErrors[name] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        }

        if (!values[id]) {
            legalEntityExistingErrors[id] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        }

        if (!!values.legalEntityIdFieldFormatError) {
            legalEntityExistingErrors[id] = translations.validationMessages.LEGAL_ENTITY_NUMBER_INVALID_FORMAT;
            legalEntityIsValid = false;
        }

        if (!values[vat] || values[vat] === '') {
            legalEntityExistingErrors[vat] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        }

        if (!!values.VATFormatError) {
            legalEntityExistingErrors[vat] = translations.validationMessages.VAT_INVALID_FORMAT;
            legalEntityIsValid = false;
        }

        if (
            values[country] !== constValues.countrySerbiaId.toString(10) &&
            values[country] !== constValues.countrySerbiaId &&
            values[city] === ''
        ) {
            legalEntityExistingErrors[city] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        }

        if (
            (values[country] === constValues.countrySerbiaId.toString(10) ||
                values[country] === constValues.countrySerbiaId) &&
            (!values[cityInSerbia] || values[cityInSerbia].id === '')
        ) {
            legalEntityExistingErrors[cityInSerbia] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        }

        if (values[street] === '') {
            legalEntityExistingErrors[street] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        }

        if (values[streetNumber] === '') {
            legalEntityExistingErrors[streetNumber] = translations.validationMessages.REQUIRED_FIELD;
            legalEntityIsValid = false;
        }

        if (values[email] && !checkEmail(values[email])) {
            legalEntityExistingErrors[email] = translations.validationMessages.EMAIL_ADDRESS_FORMAT;
            legalEntityIsValid = false;
        }
    }

    return { errors: legalEntityExistingErrors, touched: legalEntityTouched, isValid: legalEntityIsValid };
};
